import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import Modal from '../../components/UI/Modal';
import ConfirmDialog from '../../components/UI/ConfirmDialog';
import { publicationsAPI } from '../../services/api';
import { getStorageUrl } from '../../utils/imageHelpers';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Eye, 
  Download, 
  Upload,
  FileText,
  File,
  Calendar,
  User,
  Tag,
  X,
  BookOpen,
  Award,
  Globe
} from 'lucide-react';

const PublicationsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.heading};
  margin: 0;
`;

const SearchContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
`;

const PublicationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PublicationCard = styled(Card)`
  padding: 1.5rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  border: 1px solid ${props => props.theme.colors.border || '#e0e0e0'};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  
  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    border-color: ${props => props.theme.colors.primary}40;
  }
`;

const PublicationHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const PublicationIcon = styled.div`
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}15, ${props => props.theme.colors.primary}30);
  border-radius: ${props => props.theme.borderRadius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.primary};
  flex-shrink: 0;
  box-shadow: 0 2px 8px ${props => props.theme.colors.primary}20;
`;

const PublicationInfo = styled.div`
  flex: 1;
`;

const PublicationTitle = styled.h3`
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.heading};
  margin: 0 0 0.5rem 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PublicationMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  margin-bottom: 1rem;
`;

const PublicationDescription = styled.p`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  margin: 0 0 1rem 0;
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-grow: 1;
`;

const PublicationActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: auto;
  padding-top: 1rem;
  border-top: 1px solid ${props => props.theme.colors.border || '#f0f0f0'};
`;

const CategoryTag = styled.span`
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}15, ${props => props.theme.colors.primary}25);
  color: ${props => props.theme.colors.primary};
  padding: 0.35rem 0.75rem;
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: 1px solid ${props => props.theme.colors.primary}30;
`;

const FileTypeBadge = styled.span`
  background-color: ${props => {
    switch (props.type) {
      case 'pdf':
        return props.theme.colors.error + '15';
      case 'doc':
      case 'docx':
        return props.theme.colors.primary + '15';
      case 'ppt':
      case 'pptx':
        return props.theme.colors.warning + '15';
      default:
        return props.theme.colors.text.secondary + '15';
    }
  }};
  color: ${props => {
    switch (props.type) {
      case 'pdf':
        return props.theme.colors.error;
      case 'doc':
      case 'docx':
        return props.theme.colors.primary;
      case 'ppt':
      case 'pptx':
        return props.theme.colors.warning;
      default:
        return props.theme.colors.text.secondary;
    }
  }};
  padding: 0.35rem 0.75rem;
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: 1px solid ${props => {
    switch (props.type) {
      case 'pdf':
        return props.theme.colors.error + '30';
      case 'doc':
      case 'docx':
        return props.theme.colors.primary + '30';
      case 'ppt':
      case 'pptx':
        return props.theme.colors.warning + '30';
      default:
        return props.theme.colors.text.secondary + '30';
    }
  }};
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.primary};
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border.primary};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  color: ${props => props.theme.colors.text.primary};
  background-color: ${props => props.theme.colors.background.primary};
  resize: vertical;
  min-height: 100px;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary + '20'};
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border.primary};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  color: ${props => props.theme.colors.text.primary};
  background-color: ${props => props.theme.colors.background.primary};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary + '20'};
  }
`;

const FileUpload = styled.div`
  border: 2px dashed ${props => props.theme.colors.border.primary};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s ease-in-out;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
  }
`;

const FilePreview = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background-color: ${props => props.theme.colors.background.secondary};
  border-radius: ${props => props.theme.borderRadius.md};
  margin-bottom: 1rem;
`;

const FileInfo = styled.div`
  flex: 1;
`;

const FileName = styled.div`
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: 0.25rem;
`;

const FileSize = styled.div`
  font-size: ${props => props.theme.typography.fontSize.xs};
  color: ${props => props.theme.colors.text.secondary};
`;

const RemoveFileButton = styled.button`
  background-color: ${props => props.theme.colors.error};
  color: ${props => props.theme.colors.white};
  border: none;
  border-radius: ${props => props.theme.borderRadius.full};
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const ViewerModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;
`;

const ViewerModalContent = styled.div`
  background: ${props => props.theme.colors.background.primary};
  border-radius: ${props => props.theme.borderRadius.lg};
  width: 95%;
  height: 90vh;
  max-width: 1400px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const ViewerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid ${props => props.theme.colors.border || '#e0e0e0'};
  background: ${props => props.theme.colors.background.secondary};
`;

const ViewerTitle = styled.h3`
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0;
  max-width: 80%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ViewerCloseButton = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.theme.colors.text.secondary};
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${props => props.theme.borderRadius.sm};
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.colors.error}15;
    color: ${props => props.theme.colors.error};
  }
`;

const ViewerFrame = styled.iframe`
  flex: 1;
  width: 100%;
  border: none;
  background: white;
`;

const ViewerInfo = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  padding: 1rem 1.5rem;
  background: ${props => props.theme.colors.background.secondary};
  border-top: 1px solid ${props => props.theme.colors.border || '#e0e0e0'};
`;

const ResearchPublications = () => {
  const { theme } = useTheme();
  const { user, hasPermission } = useAuth();
  const [publications, setPublications] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewerModal, setShowViewerModal] = useState(false);
  const [viewingDocument, setViewingDocument] = useState(null);
  const [editingPublication, setEditingPublication] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    authors: '',
    publicationDate: '',
    publisher: '',
    language: '',
    pages: '',
    isbn: '',
    doi: '',
    keywords: '',
    abstract: '',
    file: null,
    filePreview: null,
    fileType: '',
    fileSize: '',
    published: false
  });
  
  const categories = [
    { value: 'research_paper', label: 'Research Paper' },
    { value: 'report', label: 'Report' },
    { value: 'policy_brief', label: 'Policy Brief' },
    { value: 'newsletter', label: 'Newsletter' },
    { value: 'book', label: 'Book' },
    { value: 'article', label: 'Article' },
    { value: 'guideline', label: 'Guideline' },
    { value: 'manual', label: 'Manual' }
  ];
  
  const languages = [
    'English',
    'Amharic',
    'Oromo',
    'Tigrinya',
    'French',
    'Arabic'
  ];
  
  // Mock publications data
  // Fetch publications from database
  useEffect(() => {
    const fetchPublications = async () => {
      try {
        const response = await publicationsAPI.getAll();
        console.log('Fetched publications for admin:', response.data);
        const pubsData = response.data.data || response.data;
        setPublications(Array.isArray(pubsData) ? pubsData : []);
      } catch (error) {
        console.error('Error fetching publications:', error);
        setPublications([]);
      }
    };

    fetchPublications();
  }, []);
  
  const filteredPublications = publications.filter(item => {
    const matchesSearch = item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.authors?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === '' || item.publication_type === selectedCategory;
    return matchesSearch && matchesCategory;
  });
  
  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'pdf':
        return <FileText size={24} />;
      case 'doc':
      case 'docx':
        return <File size={24} />;
      case 'ppt':
      case 'pptx':
        return <File size={24} />;
      default:
        return <File size={24} />;
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === 'file') {
      const file = files[0];
      if (file) {
        const fileType = file.name.split('.').pop().toLowerCase();
        setFormData(prev => ({
          ...prev,
          file: file,
          filePreview: file.name,
          fileType: fileType,
          fileSize: (file.size / (1024 * 1024)).toFixed(2) + ' MB'
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const publicationData = {
        title: formData.title,
        description: formData.description,
        publication_type: formData.category || 'report',
        authors: formData.authors,
        publication_date: formData.publicationDate,
        publisher: formData.publisher,
        language: formData.language,
        pages: formData.pages ? parseInt(formData.pages) : null,
        isbn: formData.isbn,
        doi: formData.doi,
        keywords: formData.keywords ? formData.keywords.split(',').map(k => k.trim()) : [],
        status: formData.published ? 'published' : 'draft',
        is_downloadable: true,
        allow_preview: true,
      };

      let savedPublication;
    if (editingPublication) {
        const response = await publicationsAPI.update(editingPublication.id, publicationData);
        savedPublication = response.data;
        setPublications(prev => prev.map(item => 
          item.id === editingPublication.id ? savedPublication : item
        ));
    } else {
        const response = await publicationsAPI.create(publicationData);
        savedPublication = response.data;
        setPublications(prev => [savedPublication, ...prev]);
      }

      // Upload file if provided
      if (formData.file && savedPublication) {
        const fileFormData = new FormData();
        fileFormData.append('file', formData.file);
        await publicationsAPI.uploadFile(savedPublication.id, fileFormData);
    }
    
    setFormData({
      title: '',
      description: '',
      category: '',
      authors: '',
      publicationDate: '',
      publisher: '',
      language: '',
      pages: '',
      isbn: '',
      doi: '',
      keywords: '',
      abstract: '',
      file: null,
      filePreview: null,
      fileType: '',
      fileSize: '',
      published: false
    });
    setShowAddModal(false);
    setEditingPublication(null);
      
      // Refresh publications list
      const response = await publicationsAPI.getAll();
      const pubsData = response.data.data || response.data;
      setPublications(Array.isArray(pubsData) ? pubsData : []);
      toast.success(editingPublication ? 'Publication updated successfully' : 'Publication created successfully');
    } catch (error) {
      console.error('Error saving publication:', error);
      console.error('Error details:', error.response?.data);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save publication. Please try again.';
      toast.error(errorMessage);
    }
  };
  
  const handleEdit = (publication) => {
    setEditingPublication(publication);
    setFormData({
      title: publication.title || '',
      description: publication.description || '',
      category: publication.publication_type || '',
      authors: publication.authors || '',
      publicationDate: publication.publication_date || '',
      publisher: publication.publisher || '',
      language: publication.language || '',
      pages: publication.pages || '',
      isbn: publication.isbn || '',
      doi: publication.doi || '',
      keywords: Array.isArray(publication.keywords) ? publication.keywords.join(', ') : '',
      abstract: publication.description || '',
      file: null,
      filePreview: publication.file_url || null,
      fileType: publication.file_type || '',
      fileSize: publication.file_size || '',
      published: publication.status === 'published'
    });
    setShowAddModal(true);
  };
  
  const openDeleteDialog = (publication) => {
    setDeleteTarget(publication);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deleteLoading) return;
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await publicationsAPI.delete(deleteTarget.id);
      setPublications(prev => prev.filter(item => item.id !== deleteTarget.id));
      toast.success('Publication deleted successfully');
    } catch (error) {
      console.error('Error deleting publication:', error);
      toast.error(error.response?.data?.message || 'Failed to delete publication. Please try again.');
    } finally {
      setDeleteLoading(false);
      closeDeleteDialog();
    }
  };
  
  const handleDownload = async (publication) => {
    try {
      const response = await publicationsAPI.download(publication.id);
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${publication.title}.${publication.file_type || 'pdf'}`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download publication');
    }
  };

  const handleView = (publication) => {
    if (publication.file_url) {
      setViewingDocument(publication);
      setShowViewerModal(true);
    } else {
      toast.info('No file available to preview');
    }
  };

  const getDocumentViewerUrl = (publication) => {
    const fileUrl = getStorageUrl(publication.file_url);
    const encodedUrl = encodeURIComponent(fileUrl);
    const fileType = publication.file_type?.toLowerCase();

    // For PDFs, use direct browser viewer
    if (fileType === 'pdf') {
      return fileUrl;
    }

    // For Office documents, use Microsoft Office Online Viewer
    if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(fileType)) {
      return `https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`;
    }

    // Fallback to Google Docs Viewer
    return `https://docs.google.com/viewer?url=${encodedUrl}&embedded=true`;
  };
  
  const removeFile = () => {
    setFormData(prev => ({
      ...prev,
      file: null,
      filePreview: null,
      fileType: '',
      fileSize: ''
    }));
  };
  
  return (
    <PublicationsContainer theme={theme}>
      <Header theme={theme}>
        <Title theme={theme}>Research Publications</Title>
        {hasPermission('add_publications') && (
          <Button 
            variant="primary" 
            onClick={() => setShowAddModal(true)}
          >
            <Plus size={20} /> Add Publication
          </Button>
        )}
      </Header>
      
      <SearchContainer theme={theme}>
        <Input
          placeholder="Search publications..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          icon={<Search size={20} />}
          style={{ minWidth: '300px' }}
        />
        <Select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
        >
          <option value="">All Categories</option>
          {categories.map(category => (
            <option key={category.value} value={category.value}>{category.label}</option>
          ))}
        </Select>
      </SearchContainer>
      
      <PublicationsGrid theme={theme}>
        {filteredPublications.map(item => (
          <PublicationCard key={item.id} theme={theme}>
            <PublicationHeader theme={theme}>
              <PublicationIcon theme={theme}>
                {getFileIcon(item.file_type || item.fileType)}
              </PublicationIcon>
              <PublicationInfo theme={theme}>
                <PublicationTitle theme={theme}>{item.title}</PublicationTitle>
                <PublicationMeta theme={theme}>
                  <div><User size={16} /> {item.authors || 'EWHRDN'}</div>
                  <div>
                    <Calendar size={16} /> 
                    {item.publication_date || item.publicationDate 
                      ? new Date(item.publication_date || item.publicationDate).toLocaleDateString() 
                      : 'N/A'}
                  </div>
                  <div><Globe size={16} /> {item.language || 'English'}</div>
                </PublicationMeta>
              </PublicationInfo>
            </PublicationHeader>
            
            <PublicationDescription theme={theme}>{item.description}</PublicationDescription>
            
            <PublicationActions theme={theme}>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <CategoryTag theme={theme}>{item.publication_type || item.category || 'report'}</CategoryTag>
                {item.file_type && (
                  <FileTypeBadge theme={theme} type={item.file_type}>
                    {item.file_type.toUpperCase()}
                </FileTypeBadge>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {item.file_url && (
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <Button 
                      variant="outline" 
                      size="small"
                      onClick={() => handleView(item)}
                      style={{ flex: 1 }}
                    >
                      <Eye size={16} /> View
                    </Button>
                    <Button 
                      variant="outline" 
                      size="small"
                      onClick={() => handleDownload(item)}
                      style={{ flex: 1 }}
                    >
                      <Download size={16} /> Download
                    </Button>
                  </div>
                )}
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <Button 
                    variant="outline" 
                    size="small"
                    onClick={() => handleEdit(item)}
                    style={{ flex: 1 }}
                  >
                    <Edit size={16} /> Edit
                  </Button>
                  <Button 
                    variant="outline" 
                    size="small"
                    onClick={() => openDeleteDialog(item)}
                    style={{ color: theme.colors.error, flex: 1 }}
                  >
                    <Trash2 size={16} /> Delete
                  </Button>
                </div>
              </div>
            </PublicationActions>
          </PublicationCard>
        ))}
      </PublicationsGrid>
      
      <Modal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setEditingPublication(null);
          setFormData({
            title: '',
            description: '',
            category: '',
            authors: '',
            publicationDate: '',
            publisher: '',
            language: '',
            pages: '',
            isbn: '',
            doi: '',
            keywords: '',
            abstract: '',
            file: null,
            filePreview: null,
            fileType: '',
            fileSize: '',
            published: false
          });
        }}
        title={editingPublication ? 'Edit Publication' : 'Add Publication'}
        size="large"
      >
        <form onSubmit={handleSubmit}>
          <ModalContent theme={theme}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <FormGroup theme={theme}>
                <Label theme={theme}>Title</Label>
                <Input
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter publication title"
                  required
                />
              </FormGroup>
              
              <FormGroup theme={theme}>
                <Label theme={theme}>Category</Label>
                <Select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Category</option>
                  {categories.map(category => (
                    <option key={category.value} value={category.value}>{category.label}</option>
                  ))}
                </Select>
              </FormGroup>
            </div>
            
            <FormGroup theme={theme}>
              <Label theme={theme}>Authors</Label>
              <Input
                name="authors"
                value={formData.authors}
                onChange={handleInputChange}
                placeholder="Enter authors (separated by commas)"
                required
              />
            </FormGroup>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <FormGroup theme={theme}>
                <Label theme={theme}>Publication Date</Label>
                <Input
                  name="publicationDate"
                  type="date"
                  value={formData.publicationDate}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              
              <FormGroup theme={theme}>
                <Label theme={theme}>Publisher</Label>
                <Input
                  name="publisher"
                  value={formData.publisher}
                  onChange={handleInputChange}
                  placeholder="Enter publisher"
                  required
                />
              </FormGroup>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <FormGroup theme={theme}>
                <Label theme={theme}>Language</Label>
                <Select
                  name="language"
                  value={formData.language}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Language</option>
                  {languages.map(language => (
                    <option key={language} value={language}>{language}</option>
                  ))}
                </Select>
              </FormGroup>
              
              <FormGroup theme={theme}>
                <Label theme={theme}>Pages</Label>
                <Input
                  name="pages"
                  value={formData.pages}
                  onChange={handleInputChange}
                  placeholder="Enter number of pages"
                />
              </FormGroup>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <FormGroup theme={theme}>
                <Label theme={theme}>ISBN</Label>
                <Input
                  name="isbn"
                  value={formData.isbn}
                  onChange={handleInputChange}
                  placeholder="Enter ISBN (optional)"
                />
              </FormGroup>
              
              <FormGroup theme={theme}>
                <Label theme={theme}>DOI</Label>
                <Input
                  name="doi"
                  value={formData.doi}
                  onChange={handleInputChange}
                  placeholder="Enter DOI (optional)"
                />
              </FormGroup>
            </div>
            
            <FormGroup theme={theme}>
              <Label theme={theme}>Keywords</Label>
              <Input
                name="keywords"
                value={formData.keywords}
                onChange={handleInputChange}
                placeholder="Enter keywords (separated by commas)"
              />
            </FormGroup>
            
            <FormGroup theme={theme}>
              <Label theme={theme}>Abstract</Label>
              <TextArea
                name="abstract"
                value={formData.abstract}
                onChange={handleInputChange}
                placeholder="Enter abstract"
                style={{ minHeight: '120px' }}
              />
            </FormGroup>
            
            <FormGroup theme={theme}>
              <Label theme={theme}>Description</Label>
              <TextArea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter publication description"
                required
              />
            </FormGroup>
            
            <FormGroup theme={theme}>
              <Label theme={theme}>Document File</Label>
              {formData.filePreview ? (
                <FilePreview theme={theme}>
                  <div style={{ color: theme.colors.primary }}>
                    {getFileIcon(formData.fileType)}
                  </div>
                  <FileInfo theme={theme}>
                    <FileName theme={theme}>{formData.filePreview}</FileName>
                    <FileSize theme={theme}>{formData.fileSize}</FileSize>
                  </FileInfo>
                  <RemoveFileButton theme={theme} onClick={removeFile}>
                    <X size={16} />
                  </RemoveFileButton>
                </FilePreview>
              ) : (
                <FileUpload theme={theme}>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.ppt,.pptx"
                    onChange={handleInputChange}
                    style={{ display: 'none' }}
                    id="file-upload"
                  />
                  <label htmlFor="file-upload">
                    <Upload size={32} style={{ marginBottom: '0.5rem' }} />
                    <div>Click to upload document</div>
                    <div style={{ fontSize: theme.typography.fontSize.sm, opacity: 0.7 }}>
                      PDF, DOC, DOCX, PPT, PPTX up to 50MB
                    </div>
                  </label>
                </FileUpload>
              )}
            </FormGroup>
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <Button 
                type="button" 
                variant="outline"
                onClick={() => setShowAddModal(false)}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary">
                {editingPublication ? 'Update' : 'Publish'} Publication
              </Button>
            </div>
          </ModalContent>
        </form>
      </Modal>

      {/* Document Viewer Modal */}
      {showViewerModal && viewingDocument && (
        <ViewerModalOverlay onClick={() => setShowViewerModal(false)}>
          <ViewerModalContent onClick={(e) => e.stopPropagation()}>
            <ViewerHeader theme={theme}>
              <ViewerTitle theme={theme}>{viewingDocument.title}</ViewerTitle>
              <ViewerCloseButton onClick={() => setShowViewerModal(false)}>
                <X size={24} />
              </ViewerCloseButton>
            </ViewerHeader>
            <ViewerFrame 
              src={getDocumentViewerUrl(viewingDocument)}
              title={viewingDocument.title}
            />
            <ViewerInfo theme={theme}>
              <span>
                <strong>Type:</strong> {viewingDocument.file_type?.toUpperCase() || 'Document'}
              </span>
              <span>•</span>
              <span>
                <strong>Authors:</strong> {viewingDocument.authors || 'N/A'}
              </span>
              <span>•</span>
              <span>
                <strong>Published:</strong> {new Date(viewingDocument.publication_date).toLocaleDateString()}
              </span>
            </ViewerInfo>
          </ViewerModalContent>
        </ViewerModalOverlay>
      )}

      <ConfirmDialog
        isOpen={deleteDialogOpen}
        title="Delete Publication"
        message={deleteTarget ? `Are you sure you want to delete "${deleteTarget.title}"? This action cannot be undone.` : 'Are you sure you want to delete this publication?'}
        confirmLabel="Delete"
        confirmVariant="danger"
        loading={deleteLoading}
        onConfirm={handleDelete}
        onCancel={closeDeleteDialog}
      />
    </PublicationsContainer>
  );
};

export default ResearchPublications;



